import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QueueService } from '../../services/queue.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { 
  faClock, faPlay, faCheck, faTimes, faSpinner, faExclamationCircle, 
  faHashtag, faCalendar, faEye, faTrash, faSync, faInfoCircle,
  faQuestionCircle, faChartBar, faLightbulb, faExclamationTriangle, faCog,
  faPause, faStepForward, faBan, faUser
} from '@fortawesome/free-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { AlertService } from '../../components/alert-service/alert-service';
import { HeaderActionsComponent } from '../../components/header-actions/header-actions.component';
import { ItemListComponent, ListConfig, ListItem } from '../../components/item-list/item-list.component';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { ModalTaskDetailsComponent } from '../../components/modal-task-details/modal-task-details.component';

@Component({
  selector: 'app-queue',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, TranslateModule, HeaderActionsComponent, ItemListComponent, PaginationComponent, ModalTaskDetailsComponent],
  templateUrl: './queue.component.html',
  styleUrls: ['./queue.component.scss']
})
export class QueueComponent implements OnInit {
  stats: { queued: number; running: number; completed: number; failed: number; cancelled: number } | null = null;
  tasks: any[] = [];
  listItems: ListItem[] = [];
  
  protected readonly Math = Math;
  
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  
  searchTerm = '';
  
  selectedTask: any | null = null;
  loading = false;
  showHelpModal = false;

  listConfig!: ListConfig;

  queueHeaderConfig = {
    showBackButton: true,
    showAddButton: false,
    showSearch: false,
    showTagFilter: false,
    addButton: {
      type: 'add' as const,
      visible: false,
      disabled: true
    },
    helpButton: {
      type: 'help' as const,
      visible: false,
      disabled: false
    }
  };

  faClock = faClock;
  faPlay = faPlay;
  faCheck = faCheck;
  faTimes = faTimes;
  faSpinner = faSpinner;
  faExclamationCircle = faExclamationCircle;
  faHashtag = faHashtag;
  faCalendar = faCalendar;
  faEye = faEye;
  faTrash = faTrash;
  faSync = faSync;
  faInfoCircle = faInfoCircle;

  faQuestionCircle = faQuestionCircle;
  faChartBar = faChartBar;
  faLightbulb = faLightbulb;
  faExclamationTriangle = faExclamationTriangle;
  faCog = faCog;
  faTrashAlt = faTrashAlt;
  faUser = faUser;
  
  faPause = faPause;
  faStepForward = faStepForward;
  faBan = faBan;

  constructor(private queueService: QueueService, private translate: TranslateService, private alertService: AlertService) {
  }

  private initListConfig(): void {
    this.listConfig = {
      itemIcon: faClock,
      showMetadata: true,
      showType: true,
      typeLabels: {
        'PENDING': 'QUEUE.STATUS.QUEUED',
        'pending': 'QUEUE.STATUS.QUEUED',
        'RUNNING': 'QUEUE.STATUS.RUNNING',
        'running': 'QUEUE.STATUS.RUNNING',
        'COMPLETED': 'QUEUE.STATUS.COMPLETED',
        'completed': 'QUEUE.STATUS.COMPLETED',
        'FAILED': 'QUEUE.STATUS.FAILED',
        'failed': 'QUEUE.STATUS.FAILED',
        'CANCELLED': 'QUEUE.STATUS.CANCELLED',
        'cancelled': 'QUEUE.STATUS.CANCELLED'
      },
      actions: [
        {
          id: 'view',
          label: 'BUTTON.VIEW',
          icon: faEye,
          cssClass: 'action-view'
        },
        {
          id: 'cancel',
          label: 'BUTTON.CANCEL.TASK',
          icon: faBan,
          cssClass: 'action-cancel',
          visible: (item: ListItem) => this.canCancelTask(item['status'] || '')
        }
      ]
    };
  }

  ngOnInit(): void {
    this.initListConfig();
    this.refreshData();
  }

  refreshData(): void {
    this.carregarEstatisticas();
    this.carregarTarefas();
  }

  carregarEstatisticas(): void {
    this.queueService.getQueueStats().subscribe({
      next: (response) => {
        this.stats = response.stats;
      },
      error: (err: any) => {
        this.alertService.showError(err);
      }
    });
  }

  carregarTarefas(): void {
    this.loading = true;
    this.queueService.getQueueTasks(this.searchTerm, this.currentPage, this.pageSize).subscribe({
      next: (response: any) => {
        this.tasks = response.tasks || [];
        this.totalItems = response.total || 0;
        this.transformTasks();
        this.loading = false;
      },
      error: (err: any) => {
        this.alertService.showError(err);
        this.tasks = [];
        this.listItems = [];
        this.totalItems = 0;
        this.loading = false;
      }
    });
  }

  private transformTasks(): void {
    this.listItems = this.tasks.map(task => {
      return {
        id: task.id,
        name: task.function_name || task.id,
        subname: task.user_email,
        type: task.status,
        status: task.status,
        created_date: task.started_at ? this.formatDateOnly(task.started_at) : this.formatDateOnly(task.created_at),
        created_time: task.started_at ? this.formatTimeOnly(task.started_at) : this.formatTimeOnly(task.created_at),
        raw: task
      };
    });
  }

  private formatDateOnly(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  private formatTimeOnly(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  onSearch(event: any): void {
    this.searchTerm = event.target.value.toLowerCase();
    this.currentPage = 1;
    this.carregarTarefas();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.carregarTarefas();
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    const { action, item } = event;
    if (!item.id) return;
    switch (action) {
      case 'view':
        this.viewTaskDetails(item.id.toString());
        break;
      case 'cancel':
        this.deleteTask(item.id.toString());
        break;
    }
  }

  viewTaskDetails(taskId: string): void {
    this.queueService.getTaskDetails(taskId).subscribe({
      next: (response: any) => {
        this.selectedTask = response.task;
      },
      error: (err: any) => {
        this.alertService.showError(err);
      }
    });
  }

  clearOldJobs(): void {
    if (confirm(this.translate.instant('QUEUE.CONFIRM_CLEAR_OLD'))) {
      this.queueService.clearOldJobsOneDayBack().subscribe({
        next: (response: any) => {
          this.alertService.showSuccess(response.message);
          this.refreshData();
        },
        error: (err: any) => {
          this.alertService.showError(err);
        }
      });
    }
  }

  closeModal(): void {
    this.selectedTask = null;
  }

  deleteTask(taskId: string): void {
    if (confirm(this.translate.instant('QUEUE.CONFIRM_DELETE_TASK'))) {
      this.queueService.deleteTask(taskId).subscribe({
        next: () => {
          this.tasks = this.tasks.filter(task => task.id !== taskId);
        },
        error: (err: any) => {
          this.alertService.showError(err);
        }
      });
    }
  }

  openHelpModal() {
    this.showHelpModal = true;
  }

  closeHelpModal() {
    this.showHelpModal = false;
  }

  canCancelTask(status: string): boolean {
    const cancelableStates = ['PENDING', 'pending', 'RUNNING', 'running'];
    return cancelableStates.includes(status);
  }

  goBack() {
    window.history.back();
  }
  
}